import React from 'react';
import { Box, Grid, Card, CardContent, Typography, Stack, Button } from '@mui/material';
import { Launch } from '@mui/icons-material';

interface Proyecto {
  id: string;
  titulo: string;
  descripcion: string;
  cta: string;
  url: string;
  color: string;
}

const proyectos: Proyecto[] = [
  {
    id: 'inmobot',
    titulo: 'Inmobot',
    descripcion: 'CRM inmobiliario con IA: capta, clasifica y cierra más propiedades.',
    cta: 'Visitar Inmobot',
    url: 'https://www.inmobot.cl/',
    color: '#2563EB',
  },
  {
    id: 'registratusbienes',
    titulo: 'RegistraTusBienes.cl',
    descripcion: 'Registra tus bienes, genera certificados digitales verificables con código QR y verifica la procedencia antes de comprar.',
    cta: 'Visitar RegistraTusBienes',
    url: 'https://registratusbienes.cl/',
    color: '#0F766E',
  },
];

/** Captura de la página vía thum.io (mismo servicio que usaba la publicidad del dashboard). */
const screenshot = (url: string) => `https://image.thum.io/get/width/800/crop/900/${url}`;

const ProyectoCard: React.FC<{ p: Proyecto }> = ({ p }) => {
  const [imgOk, setImgOk] = React.useState(true);
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #E2E8F0' }}>
      <Box
        component="a"
        href={p.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'block', position: 'relative', height: 240, overflow: 'hidden', bgcolor: '#f1f5f9',
          '&:hover .resp-preview-overlay': { opacity: 1 },
          '&:hover img, &:hover iframe': { transform: 'scale(1.04)' },
        }}
      >
        {imgOk ? (
          <Box
            component="img"
            src={screenshot(p.url)}
            alt={`Vista de ${p.titulo}`}
            loading="lazy"
            onError={() => setImgOk(false)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block', transition: 'transform .4s ease' }}
          />
        ) : (
          <Box
            component="iframe"
            src={p.url}
            title={p.titulo}
            loading="lazy"
            sx={{ width: '200%', height: 480, border: 0, transform: 'scale(0.5)', transformOrigin: 'top left', pointerEvents: 'none', transition: 'transform .4s ease' }}
          />
        )}
        <Box
          className="resp-preview-overlay"
          sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            background: `linear-gradient(0deg, ${p.color}cc, ${p.color}33)`, color: '#fff',
            opacity: 0, transition: 'opacity .3s ease', fontWeight: 700,
          }}
        >
          <Launch fontSize="small" /> Visitar sitio
        </Box>
      </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{p.titulo}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>{p.descripcion}</Typography>
        <Box>
          <Button
            variant="outlined"
            endIcon={<Launch />}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: p.color, borderColor: p.color, '&:hover': { borderColor: p.color, bgcolor: `${p.color}11` } }}
          >
            {p.cta}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const OtrosProyectos: React.FC = () => (
  <Box>
    <Typography variant="h4" sx={{ mb: 0.5 }}>Otros proyectos de mGeek</Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      Conoce otras plataformas desarrolladas por el mismo equipo.
    </Typography>
    <Grid container spacing={3}>
      {proyectos.map((p) => (
        <Grid item xs={12} md={6} key={p.id}>
          <ProyectoCard p={p} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

export default OtrosProyectos;
